import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LogIn, ChevronDown, Home, BookOpen, HelpCircle, FileText, Users } from 'lucide-react';

// Shared nav typography so every public nav renders links at the same size/weight/color
export const NAV_LINK_CLASS =
  'text-[#1E3A4C] hover:bg-[#1E3A4C]/10 text-sm lg:text-base font-medium px-2 lg:px-4 whitespace-nowrap';
export const NAV_CTA_CLASS =
  'text-white shadow-lg text-sm lg:text-base font-medium px-3 lg:px-4 whitespace-nowrap';

export default function PublicNav() {
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isLearnMoreActive = ['/rules', '/faq', '/blog', '/about'].some(p =>
    location.pathname === p || location.pathname.startsWith('/blog/')
  );

  return (
    <>
      {/* Top announcement bar - hidden until Season 51 air date is announced */}
      {/* Desktop Navigation */}
      <nav className="relative z-20 container mx-auto px-4 py-3 sm:py-4" data-testid="public-desktop-nav">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1" data-testid="public-nav-logo">
            <img src="/logo-new.png" alt="Survivor Fantasy League" className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 object-contain flex-shrink-0" />
            <div className="hidden sm:block min-w-0">
              <h1 className="text-lg sm:text-xl md:text-lg lg:text-2xl font-bold text-[#1E3A4C] truncate" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Survivor Fantasy League
              </h1>
              <p className="text-[#FF7F50] text-xs lg:text-sm font-medium whitespace-nowrap">Outwit • Outplay • Outlast</p>
            </div>
            {/* Mobile logo text */}
            <div className="sm:hidden">
              <h1 className="text-base font-bold text-[#1E3A4C]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Survivor League
              </h1>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2 flex-shrink-0">
            <Link to="/how-it-works" data-testid="nav-how-it-works">
              <Button
                variant="ghost"
                className={`${NAV_LINK_CLASS} ${isActive('/how-it-works') ? 'bg-[#1E3A4C]/10' : ''}`}
              >
                How It Works
              </Button>
            </Link>

            {/* Learn More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`${NAV_LINK_CLASS} ${isLearnMoreActive ? 'bg-[#1E3A4C]/10' : ''}`}
                  data-testid="nav-learn-more-trigger"
                >
                  Learn More
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48" data-testid="nav-learn-more-menu">
                <DropdownMenuItem asChild>
                  <Link to="/rules" className="flex items-center gap-2 cursor-pointer" data-testid="nav-rules">
                    <FileText className="w-4 h-4" />
                    Rules & Scoring
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/faq" className="flex items-center gap-2 cursor-pointer" data-testid="nav-faq">
                    <HelpCircle className="w-4 h-4" />
                    FAQ
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/blog" className="flex items-center gap-2 cursor-pointer" data-testid="nav-blog">
                    <BookOpen className="w-4 h-4" />
                    Blog
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/about" className="flex items-center gap-2 cursor-pointer" data-testid="nav-about">
                    <Users className="w-4 h-4" />
                    About
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/login" data-testid="nav-login-desktop">
              <Button className={`${NAV_CTA_CLASS} ml-1 lg:ml-2`} style={{ backgroundColor: '#FF7F50' }}>
                <LogIn className="w-4 h-4 mr-2" />
                Log In
              </Button>
            </Link>
          </div>

          {/* Mobile: Just Log In button (nav is at bottom) */}
          <div className="md:hidden">
            <Link to="/login" data-testid="nav-login-mobile">
              <Button size="sm" className="text-white shadow-lg text-sm font-medium" style={{ backgroundColor: '#FF7F50' }}>
                <LogIn className="w-4 h-4 mr-1" />
                Log In
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <MobileBottomNav />
    </>
  );
}

// Mobile Bottom Nav — public variant (Home / How To / Rules / FAQ / Blog)
export function MobileBottomNav() {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  return (
    <div
      className="md:hidden fixed left-3 right-3 z-50 rounded-full overflow-hidden"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom) + 12px)',
        background: 'rgba(255, 255, 255, 0.25)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow:
          '0 10px 30px rgba(30, 58, 76, 0.18), 0 2px 6px rgba(30, 58, 76, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
      }}
      data-testid="public-mobile-bottom-nav"
    >
      <div className="flex items-center justify-around py-2 px-1">
        <MobileNavItem to="/" icon={Home} label="Home" active={isActive('/')} />
        <MobileNavItem to="/how-it-works" icon={BookOpen} label="How To" active={isActive('/how-it-works')} />
        <MobileNavItem to="/rules" icon={FileText} label="Rules" active={isActive('/rules')} />
        <MobileNavItem to="/faq" icon={HelpCircle} label="FAQ" active={isActive('/faq')} />
        <MobileNavItem
          to="/blog"
          icon={BookOpen}
          label="Blog"
          active={isActive('/blog') || location.pathname.startsWith('/blog/')}
        />
      </div>
    </div>
  );
}

function MobileNavItem({ to, icon: Icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center px-3 py-1 rounded-lg transition-colors ${
        active
          ? 'text-[#FF7F50]'
          : 'text-[#1E3A4C]/70 hover:text-[#1E3A4C]'
      }`}
      data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
      <span className={`text-xs mt-0.5 ${active ? 'font-semibold' : ''}`}>{label}</span>
    </Link>
  );
}
